import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/LegalLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Copyright, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ipTypes = ["Trademark", "Copyright", "Patent", "Design right", "Other"];

export default function ReportIPViolation() {
  useEffect(() => { document.title = "Report an IP Violation — Xurray"; }, []);
  const [form, setForm] = useState({
    reporter_name: "", company: "", email: "", rights_holder: "",
    ip_type: "", infringing_url: "", description: "", declaration: false,
  });
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ip_type) return toast.error("Please select the type of intellectual property.");
    if (!form.declaration) return toast.error("Please confirm the declaration of good faith.");
    setLoading(true);
    try {
      const { data } = await api.post("/support/ip-violation", form);
      setRef(data.ref);
      toast.success("Your IP report has been submitted.");
    } catch {
      toast.error("Could not submit your report. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Layout hideAd>
      <div data-testid="ip-violation-page">
        <PageHero
          title="Report an IP Violation"
          eyebrow="Trust & Safety"
          icon={Copyright}
          intro="Rights holders and their authorised agents can report listings that infringe a trademark, copyright, patent or design right. We act on valid notices promptly."
        />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          {ref ? (
            <div className="rounded-2xl border border-green-500/30 bg-green-500/5 p-8 animate-fade-up" data-testid="ip-violation-success">
              <CheckCircle2 className="w-10 h-10 text-green-600 mb-3" />
              <h2 className="font-display font-bold text-xl">Notice received</h2>
              <p className="text-sm text-muted-foreground mt-1">Your reference number is <strong className="text-foreground" data-testid="ip-violation-ref">{ref}</strong>. Our compliance team will review your notice and may contact you for verification.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8 space-y-5 animate-fade-up" data-testid="ip-violation-form">
              <div><Label htmlFor="ip-name">Your name</Label><Input id="ip-name" required value={form.reporter_name} onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} className="mt-1.5" data-testid="ip-reporter" /></div>
              <div><Label htmlFor="ip-company">Company / organisation (optional)</Label><Input id="ip-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5" data-testid="ip-company" /></div>
              <div><Label htmlFor="ip-email">Email</Label><Input id="ip-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="ip-email" /></div>
              <div><Label htmlFor="ip-holder">Rights holder name</Label><Input id="ip-holder" required value={form.rights_holder} onChange={(e) => setForm({ ...form, rights_holder: e.target.value })} className="mt-1.5" data-testid="ip-holder" /></div>
              <div>
                <Label>Type of intellectual property</Label>
                <Select value={form.ip_type} onValueChange={(v) => setForm({ ...form, ip_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ip-type"><SelectValue placeholder="Select IP type" /></SelectTrigger>
                  <SelectContent>{ipTypes.map((c) => <SelectItem key={c} value={c} data-testid={`ip-type-${c}`}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label htmlFor="ip-url">Infringing product URL or listing ID</Label><Input id="ip-url" required value={form.infringing_url} onChange={(e) => setForm({ ...form, infringing_url: e.target.value })} className="mt-1.5" data-testid="ip-url" /></div>
              <div><Label htmlFor="ip-desc">Describe the infringement</Label><Textarea id="ip-desc" required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="ip-description" /></div>
              <div className="flex items-start gap-3 rounded-xl bg-muted/40 p-4">
                <Checkbox id="ip-decl" checked={form.declaration} onCheckedChange={(v) => setForm({ ...form, declaration: !!v })} data-testid="ip-declaration" className="mt-0.5" />
                <Label htmlFor="ip-decl" className="text-sm font-normal text-muted-foreground leading-snug">I declare in good faith that the information provided is accurate and that I am the rights holder or authorised to act on their behalf.</Label>
              </div>
              <Button type="submit" disabled={loading} size="lg" className="rounded-full w-full sm:w-auto" data-testid="ip-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit notice"}
              </Button>
            </form>
          )}
        </main>
      </div>
    </Layout>
  );
}
